import { z } from "zod";
/**
 * Day Zero: the first run, delivered through the attention mechanism itself
 * rather than a tour layered over it. It does not re-teach what HealthOS is,
 * because onboarding's AHA already did that. It walks the member to the parts
 * of the app they have a reason to open on their first day.
 *
 *   Today        attention card  ->  Snapshot
 *   Snapshot     the page's own explainer hands them back to Today
 *   Today        their first moment
 *   Today        "Do you understand how HealthOS works?"
 *
 * Snapshot is the only stop because it is the page that shows what HealthOS
 * reads, behaviour by behaviour, before any of it has moved. Anatomy is left
 * out: it is empty on day zero.
 *
 * The walk back used to be a second stop, delivered as a toast on Snapshot
 * that landed while the page's own explainer was still open. The explainer
 * ends on that handoff now, which is the same instruction in the place the
 * member is already reading, so there is nothing left for a toast to say.
 */
export const DAY_ZERO_COMPREHENSION_KEY = "healthos.dayzero.comprehension.v1";
export const dayZeroStopIdSchema = z.enum(["snapshot"]);
export const DAY_ZERO_STOPS = [
    {
        id: "snapshot",
        key: "healthos.dayzero.stop.snapshot.v1",
        route: "/snapshot",
        title: "Your Snapshot is reading",
        body: "The five behaviours behind your score, and which way each is moving. Open it to see what HealthOS can read already.",
        action: "Open your Snapshot",
    },
];
export const dayZeroComprehensionSchema = z.object({
    understands: z.boolean(),
    answeredAt: z.string(),
});
/**
 * The page explainers the tour walks the member into. Not owned by Day Zero
 * (they fire on any first visit), but a replay clears them so the run looks
 * the way it does for a genuinely new member.
 */
export const DAY_ZERO_PAGE_EXPLAINER_KEYS = [
    "healthos.plan.explainer.v1",
    "healthos.library.explainer.v1",
    "healthos.snapshot.explainer.v1",
];
/**
 * Secondary surfaces pointed out once the run itself is over. They are not
 * stops: they teach a place rather than send the member to it, so they sit
 * outside the route and outside the rail.
 */
export const DAY_ZERO_COACHMARKS = [
    {
        id: "add",
        key: "healthos.dayzero.coach.add.v1",
        navId: "add-to-today",
        title: "Add to today",
        body: "Anything HealthOS cannot see for itself: a meal, a rough night, how you are feeling. What you add is what it reasons from.",
    },
    {
        id: "challenges",
        key: "healthos.dayzero.coach.challenges.v1",
        navId: "challenges",
        title: "Challenges",
        body: "Your company walks a route together. Every step you take already counts toward it, so there is nothing to start.",
    },
];
export const DAY_ZERO_KEYS = [
    ...DAY_ZERO_STOPS.map((stop) => stop.key),
    DAY_ZERO_COMPREHENSION_KEY,
    // Included so skip clears them too: skip means the whole first run is over,
    // coachmarks included, and a replay brings them back.
    ...DAY_ZERO_COACHMARKS.map((mark) => mark.key),
];
/**
 * Stop keys the tour no longer makes. The backfill migration that named them
 * has shipped and cannot be rewritten, so the rows it writes stay in the table
 * and are read by nothing. Listing them here keeps the backfill guard able to
 * tell a retired key from a key someone forgot to wire up.
 */
export const DAY_ZERO_RETIRED_KEYS = [
    "healthos.dayzero.stop.collection.v1",
    "healthos.dayzero.stop.plan.v1",
    "healthos.dayzero.coach.snapshot.v1",
    // The walk back to Today is the Snapshot explainer's last step now.
    "healthos.dayzero.stop.home.v1",
    "healthos.dayzero.coach.community.v1",
];
/** The stop the member is on, or null once the tour is done. */
export function dayZeroStop(state) {
    return DAY_ZERO_STOPS.find((stop) => !state.visitedKeys.has(stop.key)) ?? null;
}
// Not the coachmarks: they need a rail a narrow window never renders.
export function dayZeroAsksComprehension(state) {
    if (state.comprehensionAnswered)
        return false;
    if (dayZeroStop(state) !== null)
        return false;
    return state.companionPromptSettled;
}
//# sourceMappingURL=day-zero.js.map